import React, { useState, useEffect } from 'react';
import { FileText, Sheet, Table, X, AlertTriangle, Eye, Users, Download, RefreshCw, Star, Activity, Calendar, Search, ChevronRight, BarChart3, RefreshCcw } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import useApi from '../../../hooks/useApi';
import ReportsSection from '../ReportsSection';
import { AttendanceDistributionChart } from '../admin-dashboard/charts/AttendanceDistributionChart';
import { CounselorPerformanceChart } from '../admin-dashboard/charts/CounselorPerformanceChart';
import { SessionTrendsChart } from '../admin-dashboard/charts/SessionTrendsChart';
import { StatCard } from '../admin-dashboard/StatCard';
import { DashboardFilters } from '../admin-dashboard/filters/DashboardFilters';


const COLORS = {
  primary: "#003087",
  secondary: "#FFD100",
  accent: "#DC3545"
};

const Alert = ({ message, type = 'error', onClose }) => (
  <div className={`rounded-lg p-3 mb-4 flex items-start justify-between ${type === 'error' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'
    }`}>
    <div className="flex items-start">
      <AlertTriangle className="h-4 w-4 mr-2 mt-0.5" />
      <p className="text-sm font-medium">{message}</p>
    </div>
    <button onClick={onClose} className="ml-4 text-gray-400 hover:text-gray-500">
      <X className="h-4 w-4" />
    </button>
  </div>
);

const LoadingSpinner = () => (
  <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#003087] border-t-transparent" />
);

const formatOptions = [
  { id: 'pdf', label: 'PDF', icon: FileText },
  { id: 'csv', label: 'CSV', icon: Table },
  { id: 'json', label: 'JSON', icon: Sheet }
];

const HodDashboard = ({ role = 'hod' }) => {
  const { get, post } = useApi();
  const [viewMode, setViewMode] = useState('generate');
  const [reportType, setReportType] = useState('institutional');
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [selectedCounselor, setSelectedCounselor] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [counselors, setCounselors] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [filters, setFilters] = useState(null);
  const [state, setState] = useState({
    stats: null,
    trends: [],
    counselors: [],
    clients: [],
    attendance: [],
    loading: true,
    error: null,
    lastUpdated: null,
  });

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        if (reportType === 'counselor') {
          const response = await get('/users', { params: { role: 'counselor', limit: 100 } });
          setCounselors(response.data.data || []);
        }
        if (reportType === 'client') {
          const response = await get('/users', { params: { role: 'student', limit: 100 } });
          setClients(response.data.data || []);
        }
      } catch (error) {
        setError('Failed to load users. Please try again.');
      } finally {
        setLoadingUsers(false);
      }
    };

    setSelectedCounselor('');
    setSelectedClient('');

    if (['counselor', 'client'].includes(reportType)) {
      fetchUsers();
    }
  }, [reportType, get]);

  useEffect(() => {
    if (showPdfViewer && pdfUrl) {
      const adobeDCView = new window.AdobeDC.View({
        clientId: process.env.REACT_APP_ADOBE_CLIENT_ID,
        divId: "adobe-dc-view"
      });
      adobeDCView.previewFile({
        content: { location: { url: pdfUrl } },
        metaData: { fileName: "Report.pdf" }
      });
    }
  }, [showPdfViewer, pdfUrl]);

  const fetchData = async (filterParams = {}) => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      const [stats, trends, counselors, clients, attendance] = await Promise.all([
        get("/dashboard/stats/admin"),
        get("/dashboard/sessions/trends", { params: filterParams }),
        get("/dashboard/counselors/performance", { params: filterParams }),
        get("/dashboard/clients/progress", { params: filterParams }),
        get("/dashboard/attendance/rates", { params: filterParams }),
      ]);

      setState({
        stats: stats.data.data,
        trends: trends.data.data,
        counselors: counselors.data.data,
        clients: clients.data.data,
        attendance: attendance.data.data,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error) {
      console.error("Dashboard Data Fetch Error:", error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(filters), 300000); // 5-minute auto-refresh
    return () => clearInterval(interval);
  }, [filters]);


  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const [startDate, endDate] = dateRange;

      if (!startDate || !endDate) throw new Error('Please select a date range');
      if (endDate < startDate) throw new Error('End date must be after start date');
      if (reportType === 'counselor' && !selectedCounselor) throw new Error('Please select a counselor');
      if (reportType === 'client' && !selectedClient) throw new Error('Please select a client');

      const response = await post('/dashboard/reports', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        format: selectedFormat,
        reportType,
        ...(reportType === 'counselor' && { counselorId: selectedCounselor }),
        ...(reportType === 'client' && { clientId: selectedClient })
      }, {
        responseType: selectedFormat === 'pdf' ? 'blob' : 'text',
        headers: {
          'Accept': selectedFormat === 'pdf'
            ? 'application/pdf'
            : selectedFormat === 'csv'
              ? 'text/csv'
              : 'application/json'
        }
      });

      if (selectedFormat === 'pdf') {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        setShowPdfViewer(true);
      } else {
        const blob = new Blob(
          [response.data],
          { type: selectedFormat === 'csv' ? 'text/csv' : 'application/json' }
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        link.download = `uzima-${reportType}-report-${timestamp}.${selectedFormat}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
          <p className="text-gray-500">Counseling System Overview</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-uzima-maroon-400 focus:border-transparent"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <span className="text-sm text-gray-500">
            Last updated: {state.lastUpdated ? format(state.lastUpdated, "MMM d, h:mm a") : "Never"}
          </span>
          <button
            onClick={() => fetchData(filters)}
            disabled={state.loading}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <RefreshCcw className={`h-5 w-5 ${state.loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <DashboardFilters
        onFilter={setFilters}
        onExport={(format) => {/* Implement export handling */ }}
        isLoading={state.loading}
      />

      {/* Main Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Stats and Charts */}
        <div className="col-span-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <StatCard
              title="Total Users"
              value={state.stats?.totalUsers}
              icon={Users}
              isLoading={state.loading}
              trend={5}
              color={COLORS.primary}
            />
            <StatCard
              title="Active Counselors"
              value={state.stats?.activeCounselors}
              icon={Calendar}
              isLoading={state.loading}
              color={COLORS.secondary}
            />
          </div>

          {/* Session Trends */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-uzima-maroon-500" />
                <h2 className="font-semibold text-gray-900">Session Trends</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Details</span>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            <div className="p-6">
              <SessionTrendsChart data={state.trends} isLoading={state.loading} />
            </div>
          </div>

          {/* Counselor Performance */}
          <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
            <CounselorPerformanceChart data={state.counselors} isLoading={state.loading} />
          </div>
        </div>

        {/* Right Column - Status, Attendance, Reports */}
        <div className="col-span-4 space-y-6">


          {/* Attendance Distribution */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <AttendanceDistributionChart data={state.attendance} isLoading={state.loading} />
          </div>

          {/* Active Counselors */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-medium mb-6">Active Counselors</h2>
            <div className="space-y-4">
              {state.counselors.slice(0, 3).map((counselor, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium">
                      {counselor.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{counselor.name}</p>
                      {/* <p className="text-sm text-gray-500">
                          {counselor.completionRate}% completion rate
                        </p> */}
                    </div>
                  </div>
                  <div
                    className={`w-2 h-2 rounded-full ${counselor.completionRate >= 80 ? "bg-green-500" : "bg-yellow-500"
                      }`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Reports Section (New Addition) */}
          <ReportsSection />
        </div>
      </div>
    </div>
  );
};

export default HodDashboard;